import { DragEvent, useCallback, useRef, useState } from 'react'
import { MdOutlineDeleteOutline } from 'react-icons/md'

import { Alert, IconCloudArrowUp, IconFileText } from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  AUDIENCE_FILE_MAX_BYTES,
  AudienceFileRejection,
  formatAudienceFileSize,
  readAudienceFileSample,
  validateAudienceFile,
  validateAudienceFileSample
} from '~/modules/retailMedia/helpers/audienceFile'
import { uploadAudience } from '~/modules/retailMedia/services/campaign/audienceFile'

import * as S from './styles'

export type AudienceFileValue = {
  key: string
  name: string
  uploaded_at: string
  bucket: string
  size_bytes: number
}

type AudienceImportProps = {
  value: AudienceFileValue | null
  onChange(value: AudienceFileValue | null): void
}

const MAX_MEGABYTES = Math.round(AUDIENCE_FILE_MAX_BYTES / (1024 * 1024))

/**
 * Importação do CSV de audiência (FR-018, FR-052f, FR-052j).
 *
 * O arquivo sobe para o BFF (multipart), que é quem fala com o S3. O que fica no
 * formulário é só o vínculo: chave, nome original e instante, que é o que o domínio
 * precisa para localizar o arquivo e anexá-lo ao pedido.
 *
 * Toda recusa acontece aqui, antes de qualquer envio: formato, estrutura e o teto de
 * tamanho (FR-052o). A criação da campanha é síncrona e bloqueante, então descobrir o
 * problema lá adiante custaria o briefing inteiro.
 *
 * Leiaute conforme o protótipo (2026-09-22): quadro de instrução acima, zona de
 * arrastar-e-soltar/clicar abaixo — a mesma composição que
 * `~/components/unform Input/FileInput` usa para os criativos, um pouco acima na
 * mesma tela, só que com tokens do Shoreline em vez do tema do styled-components:
 * esta área e a de segmentação em volta já são inteiramente Shoreline (Checkbox,
 * RadioGroup), e misturar os dois sistemas de token no mesmo card teria sido pior
 * para quem for dar manutenção do que a pequena duplicação de um quadro de
 * arrastar-e-soltar.
 */
const AudienceImport: React.FC<AudienceImportProps> = ({ value, onChange }) => {
  const { t } = useAPPTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const rejectionMessage = useCallback(
    (rejection: AudienceFileRejection) =>
      t(
        `rm:campaign.create.AudienceTargeting.audienceImport.errors.${rejection}`,
        { max: MAX_MEGABYTES }
      ),
    [t]
  )

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return

      setError(null)

      const fileRejection = validateAudienceFile({
        name: file.name,
        size: file.size
      })

      if (fileRejection) {
        onChange(null)
        setError(rejectionMessage(fileRejection))
        return
      }

      setIsUploading(true)

      try {
        const sample = await readAudienceFileSample(file)
        const sampleRejection = validateAudienceFileSample(sample)

        if (sampleRejection) {
          onChange(null)
          setError(rejectionMessage(sampleRejection))
          return
        }

        const result = await uploadAudience(file)

        onChange({
          key: result.key,
          name: file.name,
          uploaded_at: new Date().toISOString(),
          bucket: result.bucket,
          size_bytes: file.size
        })
      } catch {
        // A mensagem do armazenamento não diz nada ao operador (e a URL assinada
        // não deve aparecer em lugar nenhum) — o que serve aqui é "tente de novo".
        onChange(null)
        setError(
          t('rm:campaign.create.AudienceTargeting.audienceImport.errors.upload')
        )
      } finally {
        setIsUploading(false)
      }
    },
    [onChange, rejectionMessage, t]
  )

  const handleSelectFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      // Zera o input a cada escolha: sem isso, escolher o mesmo arquivo de novo
      // depois de um erro não dispara `change` e a tela fica presa no erro antigo.
      event.target.value = ''

      handleFile(file)
    },
    [handleFile]
  )

  const handleDragOver = useCallback((event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsDraggingOver(true)
  }, [])

  const handleDragLeave = useCallback(() => setIsDraggingOver(false), [])

  const handleDrop = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setIsDraggingOver(false)
      handleFile(event.dataTransfer.files?.[0])
    },
    [handleFile]
  )

  const handleRemove = useCallback(() => {
    setError(null)
    onChange(null)
  }, [onChange])

  const dropZoneActionLabel = t(
    `rm:campaign.create.AudienceTargeting.audienceImport.actions.${
      isUploading ? 'uploading' : 'select'
    }`
  )

  return (
    <S.Wrapper>
      <Alert variant="informational">
        {t('rm:campaign.create.AudienceTargeting.audienceImport.helperText', {
          max: MAX_MEGABYTES
        })}
      </Alert>

      <S.HiddenInput
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        data-testid="audience-import-input"
        onChange={handleSelectFile}
      />

      {value && !isUploading ? (
        <S.FileCard>
          <S.FileIcon aria-hidden="true">
            <IconFileText />
          </S.FileIcon>

          <S.FileMeta>
            <S.FileName title={value.name}>{value.name}</S.FileName>
            <S.FileSize>{formatAudienceFileSize(value.size_bytes)}</S.FileSize>
          </S.FileMeta>

          {/* Mesmo componente/template da exclusão de criativo
              (OffsiteCreativesList) -- só o texto muda. O tipo já é
              "button" por padrão nele, ao contrário do Button do Shoreline
              usado no resto deste arquivo. */}
          <Button
            onClick={handleRemove}
            template="transparentCritical"
            size="xsmall"
            iconLeft={<MdOutlineDeleteOutline />}
            text={t(
              'rm:campaign.create.AudienceTargeting.audienceImport.actions.remove'
            )}
          />
        </S.FileCard>
      ) : (
        <S.DropZone
          type="button"
          $isDraggingOver={isDraggingOver}
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <S.DropZoneIcon>
            <IconCloudArrowUp />
          </S.DropZoneIcon>
          <S.DropZoneAction>{dropZoneActionLabel}</S.DropZoneAction>
          {!isUploading && (
            <S.DropZoneHint>
              {t(
                'rm:campaign.create.AudienceTargeting.audienceImport.actions.dragHint'
              )}
            </S.DropZoneHint>
          )}
        </S.DropZone>
      )}

      {error && <S.ErrorText role="alert">{error}</S.ErrorText>}
    </S.Wrapper>
  )
}

export default AudienceImport
